import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import { TAB_LABELS } from './types';
import type {
  VisibleRow,
  ResourcePickerCategory,
  ResourcePickerItem,
  ResourcePickerMode,
  ResourcePickerTab,
} from './types';
import type { UseResourcePickerStateResult } from './use-resource-picker-state';
import { useResourcePickerState } from './use-resource-picker-state';
import { ResourcePickerSearch } from './resource-picker-search';
import { ResourcePickerRow } from './resource-picker-row';
import { ResourcePickerFooter } from './resource-picker-footer';

// ─── Props ──────────────────────────────────────────────────────────

export type ResourcePickerContentProps<T = unknown> = {
  /** Items to display. */
  items: ResourcePickerItem<T>[];
  /** Single-select (mention insert) or multi-select (bulk attach). */
  mode?: ResourcePickerMode;
  /** Called when an item is selected in single-select mode. */
  onSelect?: (item: ResourcePickerItem<T>) => void;
  /** Called when the user confirms multi-select. */
  onMultiConfirm?: (items: ResourcePickerItem<T>[]) => void;
  /** Called when the picker should close. */
  onDismiss?: () => void;
  /** Leaves the picker for the full catalogue. Omit to hide that row. */
  onBrowseAll?: () => void;
  /** Restrict available tabs. */
  availableTabs?: ResourcePickerTab[];
  /** Initial active tab. */
  initialTab?: ResourcePickerTab;
  /** Category icons (passed through to group headers). */
  categoryIcons?: Partial<Record<ResourcePickerCategory, React.ReactNode>>;
  /** Provider logos keyed by provider id, shown on the tool provider rows. */
  providerIcons?: Map<string, React.ReactNode>;
  /** Whether the data is still loading. */
  isLoading?: boolean;
  /** Renders the picker's own search box. */
  showSearchInput?: boolean;
  /** Search placeholder text. */
  searchPlaceholder?: string;
  /** Multi-select action button label. */
  actionLabel?: string;
  /** Contextual label in the footer, e.g. "Attaching to X". */
  contextLabel?: string;
  /** CSS class for the outer container. */
  className?: string;
  /** Max height CSS value. */
  maxHeight?: string;
  /** Fixed width CSS value (for mention popover sizing). */
  width?: number | string;
  /**
   * External keyboard handler. When provided, keyboard events are first
   * forwarded here; if not handled, the picker's own handler runs.
   */
  onKeyDown?: (event: React.KeyboardEvent) => boolean;
};

/**
 * Standalone resource picker content. Pre-wired variant that internally creates
 * its own `useResourcePickerState`. Use this when you want a self-contained
 * picker. For cases where you manage state externally (e.g. the TipTap adapter),
 * use `ResourcePickerContentControlled` instead.
 */
export const ResourcePickerContent = <T,>(
  props: ResourcePickerContentProps<T>
): React.ReactElement => {
  const {
    items,
    mode = 'single',
    onSelect,
    onMultiConfirm,
    onDismiss,
    availableTabs,
    initialTab,
    categoryIcons,
    providerIcons,
    ...rest
  } = props;
  // Presence of the handler is what decides whether the row exists, so the two
  // cannot drift into a row that does nothing or a handler nothing can reach.
  const browseAllLabel =
    props.onBrowseAll !== undefined ? 'Browse all tools' : undefined;

  const picker = useResourcePickerState({
    items,
    mode,
    initialTab,
    availableTabs,
    categoryIcons,
    providerIcons,
    browseAllLabel,
  });

  return (
    <ResourcePickerContentControlled
      picker={picker}
      mode={mode}
      onSelect={onSelect}
      onMultiConfirm={onMultiConfirm}
      onDismiss={onDismiss}
      {...rest}
    />
  );
};

// ─── Controlled variant (external state) ────────────────────────────

export type ResourcePickerContentControlledProps<T = unknown> = {
  picker: UseResourcePickerStateResult<T>;
  mode: ResourcePickerMode;
  onSelect?: (item: ResourcePickerItem<T>) => void;
  onMultiConfirm?: (items: ResourcePickerItem<T>[]) => void;
  onDismiss?: () => void;
  onBrowseAll?: () => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  actionLabel?: string;
  contextLabel?: string;
  className?: string;
  maxHeight?: string;
  width?: number | string;
  searchQuery?: string;
  showSearchInput?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => boolean;
};

export const ResourcePickerContentControlled = <T,>(
  props: ResourcePickerContentControlledProps<T>
): React.ReactElement => {
  const {
    picker,
    mode,
    onSelect,
    onMultiConfirm,
    onDismiss,
    onBrowseAll,
    isLoading,
    searchPlaceholder,
    actionLabel,
    contextLabel,
    className,
    maxHeight = 'min(400px, 72vh)',
    width,
    searchQuery,
    showSearchInput = true,
    onKeyDown: externalKeyDown,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus the search input on mount when this picker owns search input.
  useEffect(() => {
    if (!showSearchInput) {
      return;
    }
    // Small delay so the floating-ui position settles before focusing.
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [showSearchInput]);

  useEffect(() => {
    if (searchQuery !== undefined && searchQuery !== picker.searchQuery) {
      picker.setSearch(searchQuery);
    }
  }, [picker, searchQuery]);

  // Build a stable id→item map for type-safe row → onSelect bridging.
  // The row component works with ResourcePickerItem<unknown>, so we look up
  // the original typed item by id when the row fires.
  /**
   * Focus never lands on the list: it stays in the editor or the search field,
   * and arrow keys are forwarded here. That means a screen reader only learns
   * where the cursor is if the focused control points at the active row, so
   * every navigable row needs a stable id to point at.
   */
  const listboxId = `${React.useId().replace(/[^a-zA-Z0-9]/g, '')}-picker`;
  const rowDomId = useCallback(
    (row: VisibleRow<T>): string | undefined =>
      row.kind === 'group-header'
        ? undefined
        : `${listboxId}-row-${row.navIndex}`,
    [listboxId]
  );
  const activeDescendantId = picker.visibleRows.some(
    (row) => row.kind !== 'group-header' && row.navIndex === picker.cursorIndex
  )
    ? `${listboxId}-row-${picker.cursorIndex}`
    : undefined;

  const itemsById = useMemo(() => {
    const map = new Map<string, ResourcePickerItem<T>>();
    for (const row of picker.visibleRows) {
      if (row.kind === 'item') {
        map.set(row.item.id, row.item);
      }
    }
    return map;
  }, [picker.visibleRows]);

  /**
   * At most one detail is open. It used to be per-row local state, so several
   * could be open at once until the list was mostly panels, and an open one
   * silently collapsed whenever filtering re-rendered the row.
   */
  const handleBrowseAll = useCallback(() => {
    onBrowseAll?.();
    onDismiss?.();
  }, [onBrowseAll, onDismiss]);

  const handleToggleDetail = useCallback(
    (id: string) => {
      picker.setExpandedItem(picker.expandedItemId === id ? undefined : id);
    },
    [picker]
  );

  const handleItemSelect = useCallback(
    (item: ResourcePickerItem<T>, event?: React.MouseEvent) => {
      if (mode === 'multi' || event?.shiftKey === true) {
        picker.toggleSelection(item.id);
        return;
      }

      if (mode === 'single') {
        picker.recordRecentSelection(item.id);
        onSelect?.(item);
      }
    },
    [mode, onSelect, picker]
  );

  const handleRowSelect = useCallback(
    (untypedItem: ResourcePickerItem, event: React.MouseEvent) => {
      const typedItem = itemsById.get(untypedItem.id);
      if (typedItem !== undefined) {
        handleItemSelect(typedItem, event);
      }
    },
    [itemsById, handleItemSelect]
  );

  const widthStyle = typeof width === 'number' ? `${width}px` : width;
  const multiSelectActive = mode === 'multi' || picker.selectionCount > 0;
  const showFooter = multiSelectActive && onMultiConfirm !== undefined;

  const handleMultiConfirm = useCallback(() => {
    onMultiConfirm?.(picker.selectedItems);
  }, [onMultiConfirm, picker.selectedItems]);

  const handleContainerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Let the external handler go first.
      if (externalKeyDown !== undefined && externalKeyDown(event)) {
        event.preventDefault();
        return;
      }

      const { key } = event;

      // Escape dismisses. Only claim the key when someone is listening —
      // otherwise preventDefault would swallow the enclosing modal's own
      // escape-to-close and leave the user stuck.
      if (key === 'Escape' && onDismiss !== undefined) {
        event.preventDefault();
        onDismiss();
        return;
      }

      // Backspace past an empty query dismisses, which deletes the `@` that
      // opened the menu. That only makes sense when the query came from the
      // editor; when this picker owns a real search box, backspacing in it is
      // just editing and must not close anything.
      if (
        key === 'Backspace' &&
        showSearchInput === false &&
        picker.searchQuery.length === 0
      ) {
        onDismiss?.();
        return;
      }

      // Enter on a focused item selects it.
      if (key === 'Enter') {
        event.preventDefault();
        const focusedRow = picker.visibleRows.find(
          (row) =>
            (row.kind === 'item' ||
              row.kind === 'provider-trigger' ||
              row.kind === 'show-more' ||
              row.kind === 'browse-all') &&
            row.navIndex === picker.cursorIndex
        );
        if (focusedRow !== undefined) {
          if (focusedRow.kind === 'browse-all') {
            handleBrowseAll();
          } else if (focusedRow.kind === 'show-more') {
            picker.expandCategory(focusedRow.category);
          } else if (focusedRow.kind === 'provider-trigger') {
            picker.toggleProvider(focusedRow.provider);
          } else if (focusedRow.kind === 'item') {
            if (multiSelectActive) {
              picker.toggleSelection(focusedRow.item.id);
            } else {
              handleItemSelect(focusedRow.item);
            }
          }
        }
        return;
      }

      if (key === 'ArrowRight') {
        const focusedRow = picker.visibleRows.find(
          (row) => row.kind === 'item' && row.navIndex === picker.cursorIndex
        );
        if (
          focusedRow !== undefined &&
          focusedRow.kind === 'item' &&
          focusedRow.item.detail !== undefined
        ) {
          event.preventDefault();
          picker.setExpandedItem(focusedRow.item.id);
          return;
        }
      }

      if (key === 'ArrowLeft' && picker.expandedItemId !== undefined) {
        event.preventDefault();
        picker.setExpandedItem(undefined);
        return;
      }

      // Delegate all other keys to the picker's handler.
      if (picker.handleKeyDown(event)) {
        event.preventDefault();
      }
    },
    [
      externalKeyDown,
      onDismiss,
      picker,
      multiSelectActive,
      handleItemSelect,
      handleBrowseAll,
      showSearchInput,
    ]
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-md',
        className
      )}
      style={{
        maxHeight,
        width: widthStyle,
      }}
      onKeyDown={handleContainerKeyDown}
      tabIndex={-1}
    >
      <ResourcePickerSearch
        searchQuery={picker.searchQuery}
        onSearchChange={picker.setSearch}
        activeTab={picker.activeTab}
        onTabChange={picker.setTab}
        activeTabs={picker.activeTabs}
        placeholder={searchPlaceholder}
        inputRef={inputRef}
        showInput={showSearchInput}
        listboxId={listboxId}
        activeDescendantId={activeDescendantId}
      />
      <div
        id={listboxId}
        className="flex-1 overflow-y-auto py-1"
        role="listbox"
        aria-label="Results"
        aria-multiselectable={multiSelectActive ? true : undefined}
      >
        {isLoading === true && picker.visibleRows.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
            Loading…
          </div>
        ) : picker.visibleRows.length === 0 ? (
          /*
           * Search only ever sees what the type filter left behind, so a query
           * that matches nothing of the active type returns silence and reads
           * as a broken picker. Name the constraint and offer the way out.
           */
          <div className="flex flex-col items-center gap-1 py-6 text-xs text-muted-foreground">
            {picker.activeTab !== 'all' && picker.searchQuery.trim() !== '' ? (
              <>
                <span>
                  No {TAB_LABELS[picker.activeTab].toLowerCase()} match “
                  {picker.searchQuery.trim()}”
                </span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => picker.setTab('all')}
                >
                  Search all types
                </button>
              </>
            ) : (
              <span>No results found</span>
            )}
          </div>
        ) : (
          picker.visibleRows.map((row, index) => (
            <ResourcePickerRow
              key={
                row.kind === 'group-header'
                  ? `gh-${row.category}`
                  : row.kind === 'provider-trigger'
                  ? `pt-${row.provider}`
                  : row.kind === 'item'
                  ? `i-${row.item.id}`
                  : `row-${index}`
              }
              row={row}
              rowId={rowDomId(row)}
              cursorIndex={picker.cursorIndex}
              selection={picker.selection}
              expandedItemId={picker.expandedItemId}
              multiSelect={multiSelectActive}
              onSelect={handleRowSelect}
              onToggleSelection={picker.toggleSelection}
              onToggleDetail={handleToggleDetail}
              onToggleProvider={picker.toggleProvider}
              onExpandCategory={picker.expandCategory}
              onBrowseAll={handleBrowseAll}
              onHover={picker.setCursor}
              onDismiss={onDismiss}
            />
          ))
        )}
      </div>

      {showFooter && (
        <ResourcePickerFooter
          selectionCount={picker.selectionCount}
          onConfirm={handleMultiConfirm}
          actionLabel={actionLabel}
          contextLabel={contextLabel}
        />
      )}
    </div>
  );
};
